import { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { toFiniteNumber } from '../utils/formatters';
import { supabase } from '../lib/supabaseClient';
import { fetchUserPortfolio, saveUserPortfolioPositions } from '../services/api';

const PortfolioContext = createContext();

export function PortfolioProvider({ children }) {
  const [positions, setPositions] = useState(() => {
    try {
      const saved = localStorage.getItem('hqs-portfolio');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  // Track whether the DB sync has run to avoid overwriting DB data
  // with stale localStorage on hot-reloads.
  const dbSyncedRef = useRef(false);
  // Debounce timer ref for DB saves.
  const saveTimerRef = useRef(null);

  // ── DB sync on mount / auth state change ──────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function syncFromDb() {
      try {
        const session = (await supabase?.auth?.getSession?.())?.data?.session;
        if (!session) return;

        const { data, error } = await fetchUserPortfolio();
        if (cancelled) return;
        if (error) {
          console.warn('[PortfolioContext] DB-Sync fehlgeschlagen:', error);
          return;
        }
        const dbPositions = Array.isArray(data?.positions) ? data.positions : [];
        if (dbPositions.length > 0) {
          // DB is authoritative when it has data.
          setPositions(dbPositions);
          localStorage.setItem('hqs-portfolio', JSON.stringify(dbPositions));
        } else {
          // DB empty → migrate localStorage → DB (fire-and-forget).
          const local = (() => {
            try { return JSON.parse(localStorage.getItem('hqs-portfolio') || '[]'); } catch { return []; }
          })();
          if (Array.isArray(local) && local.length > 0) {
            saveUserPortfolioPositions(local).catch((e) =>
              console.warn('[PortfolioContext] Migration fehlgeschlagen:', e)
            );
          }
        }
        dbSyncedRef.current = true;
      } catch (e) {
        console.warn('[PortfolioContext] Sync-Fehler:', e);
      }
    }

    syncFromDb();

    const { data: authListener } = supabase?.auth?.onAuthStateChange?.((event) => {
      if (event === 'SIGNED_IN') {
        dbSyncedRef.current = false;
        syncFromDb();
      } else if (event === 'SIGNED_OUT') {
        dbSyncedRef.current = false;
      }
    }) ?? { data: null };

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  // ── Persist to localStorage on every change ────────────────────────────
  useEffect(() => {
    localStorage.setItem('hqs-portfolio', JSON.stringify(positions));
  }, [positions]);

  // ── Debounced DB save helper ───────────────────────────────────────────
  const schedulDbSave = useCallback((updatedPositions) => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const session = (await supabase?.auth?.getSession?.())?.data?.session;
        if (!session) return;
        saveUserPortfolioPositions(updatedPositions).catch((e) =>
          console.warn('[PortfolioContext] DB-Speichern fehlgeschlagen:', e)
        );
      } catch { /* ignore */ }
    }, 800);
  }, []);

  const addPosition = useCallback((symbol, shares, buyPrice, buyDate = new Date()) => {
    const parsedShares = toFiniteNumber(shares, 0);
    const parsedBuyPrice = toFiniteNumber(buyPrice, 0);
    const newPosition = {
      id: Date.now(),
      symbol,
      shares: parsedShares,
      buyPrice: parsedBuyPrice,
      buyDate: buyDate.toISOString(),
      currentPrice: parsedBuyPrice
    };
    setPositions(prev => {
      const next = [...(Array.isArray(prev) ? prev : []), newPosition];
      schedulDbSave(next);
      return next;
    });
  }, [schedulDbSave]);

  /**
   * Batch-import positions from a portfolio upload.
   *
   * Each entry may carry `{ symbol, shares, entryPrice, currentPrice }`.
   * Missing shares default to 0, missing entry price defaults to the current
   * price, missing current price defaults to entry price.  Symbols that are
   * already present in the portfolio are skipped (no automatic duplicates).
   *
   * @param {Array<{symbol: string, shares?: number|null, entryPrice?: number|null, currentPrice?: number|null}>} entries
   * @returns {{ added: number, skipped: number }}
   */
  const importPositions = useCallback((entries) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      return { added: 0, skipped: 0 };
    }
    let added = 0;
    let skipped = 0;
    setPositions((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const knownSymbols = new Set(existing.map((p) => String(p.symbol || "").toUpperCase()));
      const nowIso = new Date().toISOString();
      // Use a monotonic base that is guaranteed to differ from any existing
      // position id: current time in ms multiplied by 1000 (so the last three
      // digits are free for the per-entry offset) offset by the number of
      // existing positions.  Avoids collisions when several entries are
      // processed within the same millisecond.
      const idBase = Date.now() * 1000 + existing.length;
      const next = [...existing];
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i] || {};
        const symbol = String(entry.symbol || "").trim().toUpperCase();
        if (!symbol) { skipped++; continue; }
        if (knownSymbols.has(symbol)) { skipped++; continue; }
        knownSymbols.add(symbol);
        const shares = toFiniteNumber(entry.shares, 0);
        const entryPrice = toFiniteNumber(entry.entryPrice, 0);
        const currentPrice = toFiniteNumber(entry.currentPrice, entryPrice);
        next.push({
          id: idBase + i,
          symbol,
          shares,
          buyPrice: entryPrice,
          buyDate: nowIso,
          currentPrice,
        });
        added++;
      }
      schedulDbSave(next);
      return next;
    });
    return { added, skipped };
  }, [schedulDbSave]);

  const updateCurrentPrice = useCallback((symbol, currentPrice) => {
    const parsed = toFiniteNumber(currentPrice, 0);
    setPositions(prev => {
      let changed = false;
      const next = prev.map(pos => {
        if (pos.symbol === symbol && pos.currentPrice !== parsed) {
          changed = true;
          return { ...pos, currentPrice: parsed };
        }
        return pos;
      });
      // Price updates are not saved to DB (ephemeral live data).
      return changed ? next : prev;
    });
  }, []);

  const removePosition = useCallback((id) => {
    setPositions(prev => {
      const next = (Array.isArray(prev) ? prev : []).filter(pos => pos.id !== id);
      schedulDbSave(next);
      return next;
    });
  }, [schedulDbSave]);

  const getPositionsBySymbol = useCallback((symbol) => {
    return (Array.isArray(positions) ? positions : []).filter(pos => pos.symbol === symbol);
  }, [positions]);

  const getTotalValue = useCallback(() => {
    return (Array.isArray(positions) ? positions : []).reduce((sum, pos) =>
      sum + toFiniteNumber(pos.shares, 0) * toFiniteNumber(pos.currentPrice, 0), 0
    );
  }, [positions]);

  const getTotalGainLoss = useCallback(() => {
    return (Array.isArray(positions) ? positions : []).reduce((sum, pos) =>
      sum + toFiniteNumber(pos.shares, 0) * (toFiniteNumber(pos.currentPrice, 0) - toFiniteNumber(pos.buyPrice, 0)), 0
    );
  }, [positions]);

  const getTotalGainLossPercent = useCallback(() => {
    const safePositions = Array.isArray(positions) ? positions : [];
    const totalCost = safePositions.reduce((sum, pos) =>
      sum + toFiniteNumber(pos.shares, 0) * toFiniteNumber(pos.buyPrice, 0), 0
    );
    if (totalCost === 0) return 0;
    const gainLoss = safePositions.reduce((sum, pos) =>
      sum + toFiniteNumber(pos.shares, 0) * (toFiniteNumber(pos.currentPrice, 0) - toFiniteNumber(pos.buyPrice, 0)), 0
    );
    return ((gainLoss / totalCost) * 100).toFixed(2);
  }, [positions]);

  const value = useMemo(() => ({
    positions,
    addPosition,
    importPositions,
    updateCurrentPrice,
    removePosition,
    getPositionsBySymbol,
    getTotalValue,
    getTotalGainLoss,
    getTotalGainLossPercent,
  }), [positions, addPosition, importPositions, updateCurrentPrice, removePosition, getPositionsBySymbol, getTotalValue, getTotalGainLoss, getTotalGainLossPercent]);

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within PortfolioProvider');
  }
  return context;
}
